import React, { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  deleteReferralPartner,
  fetchReferralPartnerDetail,
  markReferralPartnerPaid,
} from "../features/admin/slice-extensions/admin-slice-extension";
import {
  selectReferralPartnerDetail,
  selectReferralPartnerDetailStatus,
} from "../features/admin/admin-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import ReferralPartnerSummary from "../components/admin/ReferralPartnerSummary";
import ReferralEntriesTable from "../components/admin/ReferralEntriesTable";

/** One partner: their links, referral history, and the settle-bounties action. */
const AdminReferralPartnerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const detail = useAppSelector(selectReferralPartnerDetail);
  const status = useAppSelector(selectReferralPartnerDetailStatus);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchReferralPartnerDetail(id));
    }
  }, [dispatch, id]);

  const markPaid = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await dispatch(markReferralPartnerPaid(id)).unwrap();
      toast.success("Bounties marked as paid.");
    } catch (error) {
      toast.error((error as { message?: string })?.message || "Failed to mark the bounties paid");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!id || !window.confirm("Remove this partner? Their code stops attributing.")) return;
    setBusy(true);
    try {
      await dispatch(deleteReferralPartner(id)).unwrap();
      toast.success("Partner removed.");
      navigate(ROUTES.ADMIN_REFERRALS);
    } catch (error) {
      toast.error((error as { message?: string })?.message || "Failed to remove the partner");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <Link
                to={ROUTES.ADMIN_REFERRALS}
                className="flex items-center gap-1.5 text-sm font-medium text-primary-40 hover:underline dark:text-primary-90"
              >
                <ArrowLeft size={16} />
                All partners
              </Link>

              {status === LoadingStatus.Failed && (
                <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <p>Failed to load this partner.</p>
                </div>
              )}

              {status === LoadingStatus.Loading || !detail ? (
                status !== LoadingStatus.Failed && (
                  <p className="py-10 text-center text-grey-40 dark:text-grey-70">
                    {t("admin.loading")}
                  </p>
                )
              ) : (
                <>
                  <ReferralPartnerSummary
                    partner={detail.partner}
                    payoutThresholdMinorUnits={detail.payoutThresholdMinorUnits}
                    busy={busy}
                    onMarkPaid={markPaid}
                    onDelete={remove}
                  />
                  <h2 className="text-xl font-semibold text-grey-10 dark:text-white">
                    Referrals
                  </h2>
                  <ReferralEntriesTable referrals={detail.referrals} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminReferralPartnerPage;
